import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import DevinIcon from "src/components/ui/icons/devin";
import { getProductName } from "src/utils/brandTheme";

import DevinIntegrationForm from "./DevinIntegrationForm";

interface DevinIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const DevinInstructions: React.FC<{ productName: string }> = ({ productName }) => (
  <div className="text-muted-foreground space-y-3">
    <div className="mb-4">
      <p className="mb-2 font-bold">Generate a Devin service-user API key:</p>
      <ol className="text-sm space-y-2 list-decimal list-inside">
        <li>
          Go to{" "}
          <a
            href="https://app.devin.ai/settings/service-users"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-600 underline"
          >
            app.devin.ai → Settings → Service users
          </a>
          .
        </li>
        <li>
          Click <span className="text-amber-600">Create service user</span> and name it (e.g. {`"${productName}"`}).
        </li>
        <li>
          Grant it permission to create sessions and read sessions, then copy the generated <code className="text-amber-600">cog_...</code> key.
        </li>
        <li>
          Paste the key and your Devin organization ID into the fields below and click Test Connection.
        </li>
      </ol>
    </div>
    <div className="mb-4">
      <p className="mb-2 font-bold">Required Devin account setup:</p>
      <ul className="text-sm space-y-1 list-disc list-inside">
        <li>Devin GitHub App installed on the repos you want {productName} to delegate work to.</li>
        <li>Sufficient ACU budget on the org to cover autopilot sessions.</li>
      </ul>
    </div>
  </div>
);

export default function DevinIntegration({ isOpen, onClose, onRefetch, brandTheme }: DevinIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <DevinIcon className="h-6 w-6" />
            Connect Devin
          </DialogTitle>
          <DialogDescription>
            Let {productName} delegate proposal implementations to Devin sessions via direct API.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showInstructions ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <DevinInstructions productName={productName} />
            </div>
          </div>

          <DevinIntegrationForm onComplete={handleFormComplete} onCancel={onClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
